'use client'

import type { LocaleSwitcherCopy } from '@/app/(site)/chrome-copy'
import { cn } from '@/util/cn'
import { usePathname, useSearchParams } from 'next/navigation'
import { useId, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

type Props = {
    localeSwitcher: LocaleSwitcherCopy
    className?: string
    buttonClassName?: string
    popoverClassName?: string
}

/**
 * Menu-local language switcher. It posts to /api/locale rather than using a
 * Server Action because this chrome can render around Django-served pages too.
 *
 * The popover opens upward and hangs off the trigger's right edge. Both menu
 * footers put the trigger at the right of their layout, so opening leftward
 * keeps it inside the menu.
 */
const MenuLocaleSwitcher = ({ localeSwitcher, className, buttonClassName, popoverClassName }: Props) => {
    const { activeLocale, label, options } = localeSwitcher
    const [isOpen, setIsOpen] = useState(false)
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const ref = useRef(null)
    const formRef = useRef<HTMLFormElement>(null)
    const localeInputRef = useRef<HTMLInputElement>(null)
    const radioGroup = useId().replace(/:/g, '')

    useClickAway(ref, () => setIsOpen(false))

    const active = options.find((option) => option.value === activeLocale) ?? options[0]
    const query = searchParams.toString()
    const returnPath = `${pathname || '/'}${query ? `?${query}` : ''}`

    const choose = (value: string) => {
        const localeInput = localeInputRef.current

        if (!localeInput || value === activeLocale) {
            setIsOpen(false)
            return
        }

        localeInput.value = value
        formRef.current?.requestSubmit()
    }

    return (
        <div ref={ref} className={cn('relative inline-flex', className)}>
            {isOpen && (
                <div
                    className={cn(
                        'absolute right-0 bottom-full z-menu mb-2 flex min-w-[132px] flex-col gap-2 rounded-[4px]',
                        'bg-giphyDarkestGrey px-4 pt-4 pb-2 shadow-[0_2px_20px_0_rgba(0,0,0,0.5)]',
                        popoverClassName
                    )}
                    role="radiogroup"
                    aria-label={label}
                >
                    {options.map((option) => {
                        const isChecked = option.value === activeLocale

                        return (
                            <label
                                key={option.value}
                                className="flex cursor-pointer items-center justify-between gap-4 text-xs leading-5 font-semibold text-giphyWhite"
                            >
                                <span lang={option.value}>{option.label}</span>
                                <input
                                    type="radio"
                                    name={radioGroup}
                                    value={option.value}
                                    checked={isChecked}
                                    onChange={() => choose(option.value)}
                                    className="sr-only"
                                />
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        'relative size-[18px] shrink-0 rounded-full border-2 border-white/25',
                                        'after:absolute after:inset-[3px] after:rounded-full after:bg-white/25 after:content-[""]',
                                        isChecked && 'after:bg-giphyGreen'
                                    )}
                                />
                            </label>
                        )
                    })}
                </div>
            )}

            <form ref={formRef} action="/api/locale" method="post">
                <input type="hidden" name="returnPath" value={returnPath} />
                <input ref={localeInputRef} type="hidden" name="locale" defaultValue={activeLocale} />
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-label={label}
                    className={cn(
                        'inline-flex cursor-pointer items-center gap-1.5 rounded-[3px] border-none bg-transparent p-0',
                        'text-xs leading-4 font-semibold text-white/70 transition-colors hover:text-giphyWhite',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-giphyWhite',
                        buttonClassName
                    )}
                >
                    <span>{label}</span>
                    {active && <span className="sr-only">{`: ${active.label}`}</span>}
                    <svg
                        width="10"
                        height="6"
                        viewBox="0 0 10 6"
                        aria-hidden="true"
                        className={cn('shrink-0 transition-transform', isOpen ? 'rotate-180' : '')}
                    >
                        <path d="M1 1.5L5 5L9 1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                </button>
                <noscript>
                    <button type="submit" className="sr-only">
                        {label}
                    </button>
                </noscript>
            </form>
        </div>
    )
}

export default MenuLocaleSwitcher
